// ========================================
// TYPES PARA SIGNATURE TOKENS
// ========================================

/**
 * Interfaz principal del SignatureToken basada en schema de MongoDB
 */
export interface SignatureToken {
    _id: string;
    contractId: string;
    token: string;
    clientDni: string;
    expiresAt: string; // ISO date string
    isUsed: boolean;
    createdBy: string;
    usedAt: string | null;
    ipAddress: string | null;
    userAgent: string | null;
    metadata: {
        sentToEmail: string | null;
        deliveryMethod: 'email' | 'sms' | 'whatsapp' | 'manual';
        notes: string | null;
    };
    createdAt: string;
    updatedAt: string;
}

/**
 * Response del endpoint GET /api/firmar/:token cuando el token es VÁLIDO
 */
export interface ValidateTokenSuccessResponse {
    success: true;
    data: {
        contract: {
        id: string;
        cliente_id: string;
        suscripcion_id: string;
        uploadedAt: string; // ISO date string
        estado: 'pendiente' | 'firmado' | 'archivado';
        pdfUrl?: string | null; // URL firmada del PDF del contrato (temporal, expira en 1h)
        };
        client: {
        nombre: string;
        apellidos: string;
        dni: string;
        email: string;
        };
        token: {
        expiresIn: number; // Milisegundos hasta expiración
        expiresAt: string; // ISO date string
        };
        requiredDni: string; // DNI que debe coincidir al firmar
        /**
         * Información de la persona que realmente firmará el contrato
         * - Si tipo === 'cliente': El cliente firma su propio contrato
         * - Si tipo === 'pagador': Otra persona firma (tutor legal, pagador, etc.)
         * Campo opcional para compatibilidad con versiones anteriores del backend
         */
        firmante?: {
            nombre: string;
            dni: string;
            tipo: 'cliente' | 'pagador';
        };
    };
}

/**
 * Response del endpoint GET /api/firmar/:token cuando el token es INVÁLIDO
 */
export interface ValidateTokenErrorResponse {
    success: false;
    message: string; // "Token expirado", "Token ya utilizado", etc.
    expired?: boolean;
    used?: boolean;
}

/**
 * Union type para manejar ambas respuestas del endpoint
 */
export type ValidateTokenResponse = 
    | ValidateTokenSuccessResponse 
    | ValidateTokenErrorResponse;

/**
 * Estados posibles del token para la UI
 */
export type TokenStatus = 
  | 'loading'      // Validando token
  | 'valid'        // Token válido, mostrar formulario
  | 'expired'      // Token expirado
  | 'used'         // Token ya utilizado
  | 'notfound'     // Token no existe
  | 'error';       // Error de servidor

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Formatea el tiempo de expiración de milisegundos a texto legible
 * @param expiresIn - Milisegundos hasta expiración
 * @returns Texto formateado (ej: "23h 45m")
 */
export const formatExpiresIn = (expiresIn: number): string => {
    const hours = Math.floor(expiresIn / (1000 * 60 * 60));
    const minutes = Math.floor((expiresIn % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    
    return `${minutes}m`;
};

/**
 * Verifica si un token está expirado basándose en la fecha
 * @param expiresAt - Fecha de expiración ISO string
 * @returns true si está expirado
 */
export const isTokenExpired = (expiresAt: string): boolean => {
    return new Date(expiresAt) < new Date();
};

// ========================================
// TYPES PARA SUBMIT SIGNATURE
// ========================================

/**
 * Response del endpoint POST /api/firmar/:token cuando la firma es EXITOSA
 */
export interface SubmitSignatureSuccessResponse {
    success: true;
    message: string; // "Contrato firmado exitosamente"
    data: {
        contractId: string;
        signedAt: string; // ISO date string
        firmanteDni: string;
        estado: 'firmado' | 'parcialmente_firmado'; // Nuevo campo del backend
        firmasCompletadas: number; // Nuevo campo del backend
        firmasRequeridas: number; // Nuevo campo del backend
    };
}

/**
 * Response del endpoint POST /api/firmar/:token cuando hay ERROR
 */
export interface SubmitSignatureErrorResponse {
    success: false;
    message: string; // "DNI no coincide", "Token expirado", etc.
}

/**
 * Union type para respuestas de submit
 */
export type SubmitSignatureResponse = 
    | SubmitSignatureSuccessResponse 
    | SubmitSignatureErrorResponse;
